import { useMemo } from 'react'

import { useWindow } from '@krp/client-runtime'
import {
  FEEDBACK_SCOPES,
  ResolveScreen,
  ScannerScreen,
  aqsiSale,
  aqsiSaleOperation,
  createResolveActions,
  createScannerActions,
  makeSelectResolveVm,
  mineSeatOf,
  resolveDraftOf,
  ridKey,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { useMySession } from './useMySession'

/** Сканер окна продажи (весь экран): итог кода — из итога команд места продажи. */
export function AqsiSaleScannerBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const { session } = useMySession(resourceId)
  const registerKey = mineSeatOf(session)?.registerKey ?? null
  const feedback = useAppSelector((state: RootState) =>
    registerKey === null ? undefined : state.aqsi.feedback.byKey[ridKey(resourceId, FEEDBACK_SCOPES.sale(registerKey))],
  )
  const message =
    registerKey === null
      ? 'Вы не работаете за кассой этого кабинета — начните работу в РМК, затем сканируйте'
      : feedback?.command === 'sale.scan'
        ? feedback.note
        : null
  const actions = useMemo(() => createScannerActions(resourceId, dispatch, window.close), [dispatch, resourceId, window])
  return <ScannerScreen message={message} actions={actions} />
}

/**
 * Ручная сверка операции: модальное окно поверх продажи (rid и операция — в адресе окна). Для операции чека читается и её продажа:
 * галочки «деньги / чек» и «Пробить чек» берут из неё оплату и версию (К-128).
 */
export function AqsiSaleResolveBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const operationId = window.params.operationId ?? ''
  const args = useMemo(() => ({ resourceId, operationId }), [operationId, resourceId])
  aqsiSaleOperation.useQuerySubscription(args, { skip: resourceId === '' || operationId === '' })
  const operation = useAppSelector((state: RootState) => aqsiSaleOperation.select(args)(state).data ?? null)
  const saleId = operation?.kind === 'receipt' ? operation.saleId : null
  const saleArgs = useMemo(() => ({ resourceId, saleId: saleId ?? '' }), [resourceId, saleId])
  aqsiSale.useQuerySubscription(saleArgs, { skip: resourceId === '' || saleId === null })
  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectResolveVm()
    const selectOperation = aqsiSaleOperation.select(args)
    const selectSale = aqsiSale.select(saleArgs)
    const feedbackKey = ridKey(resourceId, FEEDBACK_SCOPES.resolve(operationId))
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        operationId,
        operation: selectOperation(state),
        sale: selectSale(state),
        draft: resolveDraftOf(state.aqsi.sale, resourceId, operationId),
        feedback: state.aqsi.feedback.byKey[feedbackKey],
      })
  }, [args, operationId, resourceId, saleArgs])
  const vm = useAppSelector(selectVm)
  const sale = useAppSelector((state: RootState) => (saleId === null ? null : (aqsiSale.select(saleArgs)(state).data ?? null)))
  const version = operation?.version ?? null
  const actions = useMemo(
    () => createResolveActions(vm, version, dispatch, window.close, { operation, sale }),
    [dispatch, operation, sale, version, vm, window],
  )
  return <ResolveScreen vm={vm} actions={actions} />
}
